/**
 * ManutencoesPage — lista de preventivas do cliente (B1.7 §5).
 *
 * Backend GET /api/v1/cliente/manutencoes filtra por X-Client-Context +
 * feature_key 'ver_manutencoes_agendadas'.
 *
 * Mostra preventivas do workflow `preventiva` ordenadas por data agendada
 * (mais próximas primeiro). Inclui: próximas + em andamento + histórico
 * recente (últimas 100). Sem ações — só read-only.
 *
 * UX: separa em 2 seções (Próximas / Realizadas) pra facilitar leitura.
 * Calendário visual fica pra rodada futura (TD-B1.7-CALENDARIO).
 */

import { useMemo } from 'react';

import { Alert, Badge, EmptyState } from '@/components/ui';
import { PortalPage, PortalCarregando } from '@/components/cliente/PortalPage';
import { useCliente } from '@/contexts/ClienteContext';
import {
  useManutencoes,
  type ManutencaoListItem,
} from '@/lib/cliente-portal-store';
import { mensagemErro } from '@/lib/mensagem-publica';
import { hojeBR } from '@/lib/datas-brasilia';

import { formatarData } from '@/lib/format-date';
export function ManutencoesPage() {
  const { clienteAtivo } = useCliente();
  const { data, isLoading, isError, error } = useManutencoes();

  const { proximas, realizadas } = useMemo(() => {
    const items = data?.items ?? [];
    const hoje = hojeBR();
    const prox = items.filter((m) => {
      // Não realizadas: dataAgendada futura OU fase ativa
      if (m.fase === 'concluida' || m.fase === 'cancelada') return false;
      if (m.dataAgendada && m.dataAgendada.slice(0, 10) < hoje) return false;
      return true;
    });
    const real = items.filter((m) => m.fase === 'concluida' || m.fase === 'cancelada');
    // Próximas: ordena ascendente (mais próxima primeiro)
    prox.sort((a, b) => (a.dataAgendada ?? '').localeCompare(b.dataAgendada ?? ''));
    // Realizadas: ordena descendente (mais recente primeiro)
    real.sort((a, b) => b.criadoEmIso.localeCompare(a.criadoEmIso));
    return { proximas: prox, realizadas: real };
  }, [data?.items]);

  return (
    <PortalPage
      testId="manutencoes-page"
      title="Manutenções preventivas"
      subtitle={
        clienteAtivo
          ? `As visitas programadas em ${clienteAtivo.nome} e o que já foi feito.`
          : 'Visitas programadas e histórico.'
      }
    >
      {!clienteAtivo || isLoading ? (
        <PortalCarregando rotulo="Carregando as preventivas…" />
      ) : isError ? (
        <Alert variant="danger">
          {mensagemErro(error, 'Não conseguimos carregar as preventivas agora. Tente atualizar a página.')}
        </Alert>
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title="Nenhuma preventiva por aqui ainda"
          description="Quando a IGM agendar a próxima visita programada, ela aparece aqui com data e situação."
        />
      ) : (
        <>
          {/* Próximas */}
          <section className="mb-8" data-testid="manutencoes-proximas">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Próximas ({proximas.length})
            </h2>
            {proximas.length === 0 ? (
              <p className="rounded-xl border border-dashed border-line px-4 py-5 text-sm text-ink-2 dark:border-line-dark dark:text-ink-dark-2">
                Nenhuma visita agendada no momento. A IGM avisa quando marcar a próxima.
              </p>
            ) : (
              <ul className="space-y-2.5">
                {proximas.map((m) => (
                  <ManutencaoCard key={m.id} manutencao={m} highlight />
                ))}
              </ul>
            )}
          </section>

          {/* Realizadas */}
          {realizadas.length > 0 && (
            <section data-testid="manutencoes-realizadas">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                Já realizadas ({realizadas.length})
              </h2>
              <ul className="space-y-2.5">
                {realizadas.map((m) => (
                  <ManutencaoCard key={m.id} manutencao={m} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </PortalPage>
  );
}

function ManutencaoCard({
  manutencao: m,
  highlight = false,
}: {
  manutencao: ManutencaoListItem;
  highlight?: boolean;
}) {
  return (
    <li
      className={`rounded-xl border bg-paper-surface p-4 dark:bg-paper-dark-surface ${
        highlight
          ? 'border-line dark:border-line-dark'
          : 'border-line-soft dark:border-line-soft-dark'
      }`}
      data-testid={`manutencao-card-${m.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {m.numeroHumano != null && (
            <span className="mb-0.5 block font-mono text-xs tabular-nums text-ink-3 dark:text-ink-dark-3">
              #{m.numeroHumano}
            </span>
          )}
          <span className="block font-medium text-ink-1 dark:text-ink-dark-1">{m.titulo}</span>
          {m.dataAgendada && (
            <span className="mt-1 block text-xs text-ink-2 dark:text-ink-dark-2">
              {highlight ? 'Agendada para ' : 'Visita de '}
              <span className="font-mono tabular-nums">{formatIsoShort(m.dataAgendada)}</span>
            </span>
          )}
        </div>
        <Badge variant={faseVariant(m.fase)} size="sm" className="shrink-0">
          {faseLabel(m.fase)}
        </Badge>
      </div>
    </li>
  );
}

function faseVariant(fase: string): 'success' | 'info' | 'warning' | 'neutral' | 'danger' {
  if (fase === 'concluida') return 'success';
  if (fase === 'cancelada') return 'danger';
  if (fase === 'em_andamento' || fase === 'em-atendimento') return 'info';
  return 'warning';
}

function faseLabel(fase: string): string {
  const map: Record<string, string> = {
    aberta: 'Agendada',
    em_andamento: 'Em andamento',
    'em-atendimento': 'Em andamento',
    concluida: 'Realizada',
    cancelada: 'Cancelada',
  };
  return map[fase] ?? fase;
}

function formatIsoShort(iso: string): string {
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
